import React from 'react';
import StoreNavigation from '../components/StoreNavigation';
import StoreFooter from '../components/StoreFooter';
import { useCart } from '../context/CartContext';
import { Newspaper, Mail, ArrowRight, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';

const Press = () => {
  const { getCartItemCount } = useCart();

  // Media coverage
  const mediaFeatures = [
    {
      outlet: 'BodyShop News',
      title: "Vesta Workwear Unveils Australia's First Female-Fit Spray Paint Suit",
      date: 'March 2026',
      description: "Vesta Workwear launches what it says is Australia's first spray suit designed for women — the patent-pending Apex Spray Suit.",
      link: 'https://bodyshopnews.net/vesta-workwear-unveils-australias-first-female-fit-spray-paint-suit/'
    },
    {
      outlet: 'Australasian Paint & Panel',
      title: 'Female-Fit Spray Suit Targets Workshops',
      date: '2026',
      description: 'A new female-fit spray suit engineered to improve comfort, movement and contamination control inside the spray booth.',
      link: 'https://www.paintandpanel.com.au/news/news/female-fit-spray-suit-targets-workshops'
    },
    {
      outlet: 'Auto Women',
      title: 'New Spray Suit Designed for Women Hits the Market',
      date: 'May 2026',
      description: 'Created in Queensland and officially launched in Townsville, the Apex suit brings comfort and safety to female automotive spray painters.',
      link: 'https://autowomen.com.au/new-spray-suit-designed-for-women-hits-the-market/'
    },
    {
      outlet: 'Motor Trader Magazine (MTAQ)',
      title: 'New Spray Suit Designed for Women Hits the Market',
      date: 'May 2026',
      description: 'The story behind Vesta Workwear and the patent-pending Apex Suit, designed with female technicians across Australia.',
      link: 'https://issuu.com/mtaq/docs/motor_trader_e-magazine_may_2026/35?ff&showOtherPublicationsAsSuggestions=true&backgroundColorFullscreen=%238c5e18'
    },
    {
      outlet: 'Motor Trader Magazine (MTAQ)',
      title: 'Auto Women: Behind the Wheel Spotlight',
      date: 'June 2026',
      description: "Founder Anne McClure is spotlighted in Auto Women's 'Behind the Wheel' feature on the women driving change in the trade.",
      link: 'https://issuu.com/mtaq/docs/motor_trader_emagazine_june_2026_v1/3?ff&showOtherPublicationsAsSuggestions=true&hideShareButton=true&backgroundColorFullscreen=%23051358'
    },
    {
      outlet: 'Townsville Bulletin',
      title: 'New Brand Launches Safety Workwear Designed for Women',
      date: 'April 2026',
      description: "Local coverage of Vesta Workwear's launch event for safety workwear purpose-designed for women in the trades.",
      link: 'https://www.townsvillebulletin.com.au/news/new-brand-launches-safety-workwear-designed-for-women/news-story/d7d64a2e45cb8fafe6ce49f89aba58ae'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <StoreNavigation cartItemCount={getCartItemCount()} />

      {/* Hero */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-[#004D51] to-[#003338] text-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <Newspaper className="w-16 h-16 mx-auto mb-6 text-[#F39C49]" />
            <h1 className="text-5xl md:text-7xl font-black mb-6">
              Vesta in the <span className="text-[#F39C49]">Media</span>
            </h1>
            <p className="text-xl text-[#CFD2D4]">
              Follow our journey as we change the game for women in trades.
            </p>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-6 py-16">
        {/* Media Features Grid */}
        {mediaFeatures.length > 0 ? (
          <section className="mb-16">
            <h2 className="text-4xl font-black text-[#004D51] mb-12 text-center">
              Featured Coverage
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {mediaFeatures.map((feature, index) => (
                <a 
                  key={index}
                  href={feature.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`press-article-${index}`}
                  className="flex flex-col bg-white border-4 border-[#004D51] rounded-xl p-6 hover:shadow-xl transition-all group"
                >
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm text-[#DB5A14] font-bold uppercase tracking-wide">{feature.outlet}</p>
                    <ExternalLink className="w-4 h-4 text-[#545759] group-hover:text-[#DB5A14] transition-colors flex-shrink-0" />
                  </div>
                  <h3 className="text-xl font-black text-[#004D51] mb-3 group-hover:text-[#DB5A14] transition-colors leading-tight">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-[#545759] mb-4 flex-1">
                    {feature.description}
                  </p>
                  {feature.date && (
                    <p className="text-xs text-[#545759] font-medium">{feature.date}</p>
                  )}
                </a>
              ))}
            </div>
          </section>
        ) : (
          <section className="mb-16">
            <div className="max-w-2xl mx-auto text-center py-12">
              <div className="w-24 h-24 bg-[#F3CEB2]/30 rounded-full flex items-center justify-center mx-auto mb-8">
                <Newspaper className="w-12 h-12 text-[#004D51]" />
              </div>
              <h2 className="text-3xl font-black text-[#004D51] mb-4">
                Watch This Space
              </h2>
              <p className="text-xl text-[#545759]">
                Vesta is just getting started. Check back soon for media coverage and features.
              </p>
            </div>
          </section>
        )}

        {/* Press Contact */}
        <section className="bg-[#F3CEB2]/20 rounded-2xl p-12 text-center">
          <Mail className="w-14 h-14 mx-auto mb-6 text-[#DB5A14]" />
          <h2 className="text-4xl font-black text-[#004D51] mb-4">
            Media Enquiries
          </h2>
          <p className="text-xl text-[#545759] mb-6 max-w-2xl mx-auto">
            For press enquiries, interview requests, or media information, please get in touch.
          </p>
          <a href="mailto:hello@vestaworkwear.com.au">
            <Button className="bg-[#DB5A14] hover:bg-[#F39C49] text-white font-black px-8 py-6 text-lg">
              hello@vestaworkwear.com.au <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </a>
        </section>

        {/* Brand Assets - for future use */}
        {/*
        <section className="mt-16">
          <h2 className="text-4xl font-black text-[#004D51] mb-8 text-center">
            Press Kit
          </h2>
          <div className="max-w-xl mx-auto bg-white border-4 border-[#004D51] rounded-xl p-8 text-center">
            <Download className="w-12 h-12 mx-auto mb-4 text-[#004D51]" />
            <h3 className="text-2xl font-black text-[#004D51] mb-2">Download Press Kit</h3>
            <p className="text-[#545759] mb-6">
              Logos, product images, founder bio, and brand guidelines.
            </p>
            <Button className="bg-[#004D51] hover:bg-[#003338] text-white font-black">
              Download ZIP
            </Button>
          </div>
        </section>
        */}
      </div>

      <StoreFooter />
    </div>
  );
};

export default Press;
